import { destinations } from "./destinations";
import { travelPackages } from "./packages";
import type {
  CompanyInfo,
  CompanyMilestone,
  CompanyValue,
  ContactChannel,
  FAQ,
  GalleryItem,
  InquiryStatusStep,
  Testimonial,
  TravelGuide,
  TravelService,
} from "../types";

const imageFor = (slug: string) => {
  const destination = destinations.find((item) => item.slug === slug);

  if (!destination) {
    throw new Error(`Missing destination data for ${slug}`);
  }

  return destination.image;
};

export { destinations, travelPackages };

export const testimonials: Testimonial[] = [
  {
    id: "testimonial-mara",
    customerName: "Mara D.",
    destination: "Boracay",
    review:
      "The trip felt easy from the first message. Our transfers were clearly explained, the hotel suited our family, and we still had enough free time to enjoy the island our way.",
    rating: 5,
    verifiedTrip: true,
    travelDate: "May 2026",
  },
  {
    id: "testimonial-paolo",
    customerName: "Paolo & Jen",
    destination: "Tokyo & Kyoto",
    review:
      "We were nervous about planning our first Japan trip, but the checklist and day-by-day advice made it manageable. The proposed route gave us structure without making every hour feel scheduled.",
    rating: 5,
    verifiedTrip: true,
    travelDate: "April 2026",
  },
  {
    id: "testimonial-camille",
    customerName: "Camille R.",
    destination: "Siargao",
    review:
      "ACES listened when we said we wanted a relaxed barkada trip. The island tour was the highlight, and the open days gave us space for cafés, surfing, and spontaneous stops.",
    rating: 5,
    verifiedTrip: true,
    travelDate: "March 2026",
  },
  {
    id: "testimonial-dela-cruz",
    customerName: "The Dela Cruz Family",
    destination: "Singapore",
    review:
      "The itinerary worked for grandparents and children, which is not easy. We appreciated the clear reminders and having one team to ask whenever our group had a question.",
    rating: 5,
    verifiedTrip: true,
    travelDate: "December 2025",
  },
  {
    id: "testimonial-angela",
    customerName: "Angela T.",
    destination: "Seoul",
    review:
      "Quick replies, practical document guidance, and a hotel location that made our free day simple. The quotation also made it clear what was and was not included.",
    rating: 5,
    verifiedTrip: true,
    travelDate: "October 2025",
  },
];

export const faqs: FAQ[] = [
  {
    id: "faq-inquiry-booking",
    category: "booking",
    question: "Is submitting an inquiry a confirmed booking?",
    answer:
      "No. An inquiry tells ACES what you are planning. Our team still needs to check current airfare, rooms, tour availability, and requirements before sending a quotation. Nothing is reserved until you accept that quotation and follow the payment instructions provided directly by ACES.",
  },
  {
    id: "faq-contact-method",
    category: "general",
    question: "How will ACES contact me?",
    answer:
      "We will use the contact method you select—phone, SMS, email, or Messenger. Keep your reference number handy so our team can quickly match the conversation to your inquiry.",
  },
  {
    id: "faq-custom-itinerary",
    category: "services",
    question: "Can I request a custom itinerary?",
    answer:
      "Yes. Choose “Custom trip” or add your preferences in the special-request field. Tell us your pace, budget range, must-see places, and any mobility or room needs so we can propose realistic options.",
  },
  {
    id: "faq-visa",
    category: "requirements",
    question: "Do you assist with visas?",
    answer:
      "ACES can help with checklists, form preparation, and application guidance for supported destinations. Visa decisions and processing times remain entirely with the relevant embassy, consulate, or authorized visa center.",
  },
  {
    id: "faq-flight-only",
    category: "services",
    question: "Can you arrange flights only?",
    answer:
      "Yes. Submit an inquiry and select Flights & Ticketing. Share your preferred dates, route, baggage needs, and flexibility so our team can check suitable options.",
  },
  {
    id: "faq-groups",
    category: "services",
    question: "Do you accept group and corporate inquiries?",
    answer:
      "Yes. We can help with family groups, school or organization trips, incentives, meetings, and corporate travel. Add the expected headcount and any special coordination needs to your inquiry.",
  },
  {
    id: "faq-prices",
    category: "payments",
    question: "How are prices confirmed?",
    answer:
      "Prices shown on this website are starting estimates. Your quotation reflects availability, travel dates, traveler mix, room arrangement, exchange rates, supplier rules, and the services you request at the time we check.",
  },
  {
    id: "faq-documents",
    category: "requirements",
    question: "What documents are required?",
    answer:
      "Requirements depend on the destination and traveler. Domestic trips generally need valid identification; international trips require a valid passport and may require a visa, proof of funds, insurance, or other documents. ACES will provide a current checklist with your quotation.",
  },
  {
    id: "faq-payment",
    category: "payments",
    question: "Can I pay through this website?",
    answer:
      "No. The website does not collect travel payments. ACES provides verified payment instructions manually after you accept a formal quotation. Never send payment to an account that has not been confirmed through an official ACES channel.",
  },
  {
    id: "faq-response-time",
    category: "general",
    question: "When should I expect a reply?",
    answer:
      "A travel consultant will normally acknowledge an inquiry during office hours, then follow up once the requested options have been checked. Complex, group, visa-sensitive, and multi-city requests may need additional research time.",
  },
];

export const services: TravelService[] = [
  {
    id: "service-flights",
    slug: "flight-ticketing",
    title: "Flights & ticketing",
    shortDescription: "Practical route and fare options with real human support.",
    description:
      "Tell us your route, dates, baggage needs, and flexibility. We will check available options and explain the important fare rules before anything is issued.",
    icon: "Plane",
    features: ["Domestic and international routes", "Baggage-aware options", "Fare-rule guidance"],
    featured: true,
  },
  {
    id: "service-hotels",
    slug: "hotel-accommodation",
    title: "Hotel accommodation",
    shortDescription: "Stays matched to location, comfort, and the way you travel.",
    description:
      "From value stays to premium escapes, we compare suitable room and location options for couples, families, groups, and business travelers.",
    icon: "Hotel",
    features: ["Room arrangement support", "Location-first recommendations", "Upgrade options"],
    featured: true,
  },
  {
    id: "service-visa",
    slug: "visa-assistance",
    title: "Visa assistance",
    shortDescription: "Clear checklists and application guidance for supported destinations.",
    description:
      "We help you organize application steps and documents while being transparent that all decisions remain with the relevant authorities.",
    icon: "BookOpenCheck",
    features: ["Document checklist", "Form guidance", "Appointment preparation"],
    featured: true,
  },
  {
    id: "service-domestic",
    slug: "domestic-tours",
    title: "Domestic tours",
    shortDescription: "Philippine getaways with transfers and experiences connected.",
    description:
      "Plan island escapes, heritage weekends, family holidays, and group journeys across the Philippines.",
    icon: "Palmtree",
    features: ["Island packages", "City and heritage trips", "Local partner coordination"],
    featured: false,
  },
  {
    id: "service-international",
    slug: "international-tours",
    title: "International tours",
    shortDescription: "Thoughtful journeys from your first passport stamp onward.",
    description:
      "Explore guided and flexible options across Asia and beyond, with practical preparation for Filipino travelers.",
    icon: "Earth",
    features: ["Guided departures", "Free-and-easy packages", "Pre-trip support"],
    featured: true,
  },
  {
    id: "service-cruises",
    slug: "cruises",
    title: "Cruises",
    shortDescription: "Cabins, sailings, and port logistics made easier to understand.",
    description:
      "We help compare routes and cabin options, then align flights, hotels, transfers, and documentation around the sailing.",
    icon: "Ship",
    features: ["Cruise selection", "Cabin guidance", "Pre- and post-cruise stays"],
    featured: false,
  },
  {
    id: "service-transfers",
    slug: "airport-transfers",
    title: "Airport transfers",
    shortDescription: "A smoother first and last mile for your journey.",
    description:
      "Request shared or private airport, hotel, pier, and terminal transfers appropriate for your party and luggage.",
    icon: "CarFront",
    features: ["Airport pick-up", "Hotel and pier transfers", "Group vehicle options"],
    featured: false,
  },
  {
    id: "service-custom",
    slug: "custom-itineraries",
    title: "Customized itineraries",
    shortDescription: "Your priorities translated into a journey that makes sense.",
    description:
      "We shape routes around your dates, pace, budget, interests, and must-see stops, then present the plan in one clear quotation.",
    icon: "Map",
    features: ["Multi-city planning", "Flexible pacing", "Interest-led experiences"],
    featured: true,
  },
  {
    id: "service-groups",
    slug: "group-corporate-travel",
    title: "Group & corporate travel",
    shortDescription: "Coordinated travel for teams, communities, and big family moments.",
    description:
      "Share your headcount, purpose, and schedule. We can organize consolidated travel options and practical group logistics.",
    icon: "UsersRound",
    features: ["Group movements", "Rooming coordination", "Dedicated planning support"],
    featured: false,
  },
];

export const travelGuides: TravelGuide[] = [
  {
    id: "guide-first-international",
    slug: "first-international-trip-checklist",
    title: "Your first international trip: a calm, practical checklist",
    excerpt:
      "A Filipino traveler’s step-by-step guide to passports, airport timing, money, connectivity, and the documents worth keeping close.",
    category: "planning-tips",
    readTime: 7,
    image: imageFor("singapore"),
    publishedAt: "2026-07-12",
    featured: true,
    sections: [
      {
        heading: "Start with the non-negotiables",
        body: ["Check your passport validity and review the official entry rules for your destination before paying for non-refundable arrangements."],
        bullets: ["Passport and visa, if required", "Confirmed itinerary", "Accommodation details", "Travel insurance if applicable"],
      },
      {
        heading: "Prepare for the airport",
        body: ["Arrive with time to spare and keep essential documents available offline. Airline and immigration requirements can change, so recheck close to departure."],
      },
    ],
  },
  {
    id: "guide-japan-visa",
    slug: "japan-visa-preparation",
    title: "Preparing for a Japan visa application",
    excerpt:
      "How to organize your documents and avoid common preparation mistakes before submitting through the appropriate channel.",
    category: "visa-guide",
    readTime: 6,
    image: imageFor("japan"),
    publishedAt: "2026-06-28",
    featured: true,
    sections: [
      {
        heading: "Use the current official checklist",
        body: ["Requirements may change and vary by purpose. Always follow the current embassy or authorized application-center instructions."],
      },
      {
        heading: "Keep the story consistent",
        body: ["Your form, itinerary, financial documents, and supporting details should describe the same realistic trip."],
      },
    ],
  },
  {
    id: "guide-island-packing",
    slug: "philippine-island-packing-list",
    title: "Pack lighter for a Philippine island escape",
    excerpt:
      "The useful essentials for boats, beach days, tropical weather, and small-airport baggage limits.",
    category: "planning-tips",
    readTime: 5,
    image: imageFor("palawan"),
    publishedAt: "2026-06-16",
    featured: false,
    sections: [
      {
        heading: "Protect the essentials",
        body: ["A compact dry bag or waterproof pouch is valuable on boat days. Bring sun protection and a reusable bottle."],
        bullets: ["Quick-dry layers", "Reef-conscious sun protection", "Waterproof document pouch", "Personal medicines"],
      },
    ],
  },
  {
    id: "guide-seoul-seasons",
    slug: "seoul-by-season",
    title: "Seoul by season: choosing the right month for you",
    excerpt:
      "A simple comparison of spring blooms, summer energy, autumn color, and winter atmosphere.",
    category: "destination-guide",
    readTime: 6,
    image: imageFor("south-korea"),
    publishedAt: "2026-05-30",
    featured: false,
    sections: [
      {
        heading: "Choose for comfort, not only photos",
        body: ["Every season changes what to pack, how long you may want outdoors, and how early popular dates should be planned."],
      },
    ],
  },
  {
    id: "guide-travel-requirements",
    slug: "how-to-check-travel-requirements",
    title: "How to recheck travel requirements with confidence",
    excerpt:
      "Where to look, what to save, and why you should verify important rules again before departure.",
    category: "travel-requirements",
    readTime: 4,
    image: imageFor("vietnam"),
    publishedAt: "2026-05-18",
    featured: true,
    sections: [
      {
        heading: "Prioritize official sources",
        body: ["Use government, embassy, immigration, airline, and authorized visa-center notices for decisions that affect entry or boarding."],
      },
      {
        heading: "Recheck close to departure",
        body: ["Save dated copies of important confirmations and verify again shortly before your trip because operational rules can change."],
      },
    ],
  },
];

export const galleryImages: GalleryItem[] = [
  {
    id: "gallery-palawan-lagoons",
    image: imageFor("palawan"),
    destination: "Palawan",
    destinationSlug: "palawan",
    caption: "Blue-water mornings among Palawan’s limestone islands",
    orientation: "portrait",
    featured: true,
  },
  {
    id: "gallery-boracay-sunset",
    image: imageFor("boracay"),
    destination: "Boracay",
    destinationSlug: "boracay",
    caption: "An easy shoreline day in Boracay",
    orientation: "landscape",
    featured: true,
  },
  {
    id: "gallery-tokyo-lights",
    image: imageFor("japan"),
    destination: "Tokyo",
    destinationSlug: "japan",
    caption: "Tokyo’s city lights after a day of discovery",
    orientation: "square",
    featured: true,
  },
  {
    id: "gallery-batanes-hills",
    image: imageFor("batanes"),
    destination: "Batanes",
    destinationSlug: "batanes",
    caption: "Open skies and green horizons in Batanes",
    orientation: "landscape",
    featured: false,
  },
  {
    id: "gallery-seoul-heritage",
    image: imageFor("south-korea"),
    destination: "Seoul",
    destinationSlug: "south-korea",
    caption: "Old and new Seoul in a single view",
    orientation: "portrait",
    featured: true,
  },
  {
    id: "gallery-vietnam-bay",
    image: imageFor("vietnam"),
    destination: "Vietnam",
    destinationSlug: "vietnam",
    caption: "A quiet passage through Vietnam’s karst scenery",
    orientation: "landscape",
    featured: false,
  },
  {
    id: "gallery-siargao-surf",
    image: imageFor("siargao"),
    destination: "Siargao",
    destinationSlug: "siargao",
    caption: "Following the island rhythm in Siargao",
    orientation: "square",
    featured: true,
  },
  {
    id: "gallery-bangkok-temple",
    image: imageFor("thailand"),
    destination: "Bangkok",
    destinationSlug: "thailand",
    caption: "Intricate temple details in Bangkok",
    orientation: "portrait",
    featured: false,
  },
  {
    id: "gallery-singapore-bay",
    image: imageFor("singapore"),
    destination: "Singapore",
    destinationSlug: "singapore",
    caption: "A golden-hour walk around Marina Bay",
    orientation: "landscape",
    featured: true,
  },
];

export const inquiryStatuses: InquiryStatusStep[] = [
  {
    status: "submitted",
    label: "Inquiry submitted",
    shortLabel: "Submitted",
    description: "Your travel preferences reached the ACES team.",
  },
  {
    status: "under-review",
    label: "Under review",
    shortLabel: "Review",
    description: "A travel consultant is checking availability and your requirements.",
  },
  {
    status: "quotation-prepared",
    label: "Quotation prepared",
    shortLabel: "Quotation",
    description: "Your proposed itinerary and current pricing are ready.",
  },
  {
    status: "waiting-for-customer",
    label: "Waiting for your response",
    shortLabel: "Your response",
    description: "Review the quotation and tell ACES whether you would like to proceed or adjust it.",
  },
  {
    status: "confirmed",
    label: "Confirmed with ACES",
    shortLabel: "Confirmed",
    description: "The reservation has been confirmed through the ACES team.",
    terminal: true,
  },
  {
    status: "cancelled",
    label: "Inquiry closed",
    shortLabel: "Closed",
    description: "This inquiry was cancelled or closed. Contact ACES if you would like to plan again.",
    terminal: true,
  },
];

export const companyInfo: CompanyInfo = {
  name: "ACES Travel",
  legalName: "ACES Travel and Tours Services",
  tagline: "Making travel dreams affordable and flexible.",
  description:
    "A DOT-accredited travel agency in Cauayan City helping travelers plan affordable, flexible domestic and international journeys since 2018.",
  email: "reservations.acestravel@gmail.com",
  phone: "0955 195 8000",
  sms: "0961 961 9000",
  messengerUrl: "https://m.me/ACESTravelAndToursServices",
  address: "Parish Pastoral Building, E. Baligod Street, Barangay District 3, Cauayan City, Isabela 3305",
  officeHours: "Message ACES before visiting for today's office hours",
  socialLinks: {
    facebook: "https://www.facebook.com/ACESTravelAndToursServices/",
  },
  placeholders: {
    dotAccreditation: "DOT-accredited travel agency",
    yearsInService: "Serving travelers since 2018",
  },
};

export const contactChannels: ContactChannel[] = [
  {
    id: "contact-messenger",
    type: "messenger",
    label: "Message us",
    value: "ACES Travel on Messenger",
    href: companyInfo.messengerUrl,
    description: "Best for quick travel questions during office hours.",
    primary: true,
  },
  {
    id: "contact-phone",
    type: "phone",
    label: "Call us",
    value: companyInfo.phone,
    href: "tel:09551958000",
    description: "Call the ACES team for reservations and travel questions.",
  },
  {
    id: "contact-sms",
    type: "sms",
    label: "Send an SMS",
    value: companyInfo.sms,
    href: "sms:09619619000",
    description: "Include your inquiry reference if you already have one.",
  },
  {
    id: "contact-email",
    type: "email",
    label: "Email us",
    value: companyInfo.email,
    href: `mailto:${companyInfo.email}`,
    description: "Useful for detailed requests and document follow-ups.",
  },
  {
    id: "contact-office",
    type: "office",
    label: "Visit the office",
    value: companyInfo.address,
    href: "#office",
    description: "Beside F.L. Dy Coliseum and near the PESO office.",
  },
];

export const companyValues: CompanyValue[] = [
  {
    id: "value-care",
    title: "Care before complexity",
    description: "We explain travel choices in plain language and keep the plan centered on the traveler.",
    icon: "HeartHandshake",
  },
  {
    id: "value-clarity",
    title: "Clear at every step",
    description: "Quotations distinguish estimates, inclusions, conditions, and next actions.",
    icon: "ListChecks",
  },
  {
    id: "value-flexibility",
    title: "Built around real lives",
    description: "We listen to budgets, schedules, comfort needs, and the pace each group can enjoy.",
    icon: "Route",
  },
  {
    id: "value-presence",
    title: "Human help matters",
    description: "Travel questions deserve a real response from a team that understands the full itinerary.",
    icon: "MessagesSquare",
  },
];

export const companyMilestones: CompanyMilestone[] = [
  {
    year: "2018",
    title: "ACES begins serving Cauayan travelers",
    description: "A local team starts making domestic and international travel more affordable and flexible.",
  },
  {
    year: "Growing journeys",
    title: "From essential bookings to complete itineraries",
    description: "ACES expands its planning support across domestic and international travel.",
  },
  {
    year: "Today",
    title: "A simpler digital inquiry experience",
    description: "Travelers can now explore ideas, shape a request, and keep one reference for follow-up.",
  },
];

export const galleryItems = galleryImages;
export const inquiryStatusSteps = inquiryStatuses;
export const packages = travelPackages;
